// Мастер «Новый пост». Тоже панель: пост заводится обратимо.
//
// Часы в сутки вводятся числом и уходят на сервер СТРОКОЙ. Запятая из привычки Excel
// принимается и заменяется точкой, но само значение до сервера доезжает строкой: превратить
// «63,5» в число с плавающей точкой значит потерять полчаса на сумме по постам, а из этой
// суммы считается норматив.
//
// Часов больше, чем 24 на каждого одновременно стоящего, — предупреждение под полем, как и в
// правке поста (`post-hours.ts`), но не запрет: решает заказчик.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiErrorWarningLine, RiTimeLine } from '@remixicon/react';
import { useLocation, useNavigate } from 'react-router';

import type { ApiError } from '@/shared/errors/api-error';
import type { PostIn } from '@/shared/api/endpoints/org';
import type { DecimalString } from '@/shared/api/types';
import { decimalString } from '@/shared/api/types';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import { parseSearch, popPanel } from '@/shared/routing/search';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import * as Input from '@/shared/ui/input';
import * as Label from '@/shared/ui/label';

import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import {
  newIdempotencyKey,
  useCreatePostMutation,
  useScheduleTypesQuery,
  useSiteQuery,
} from '@/shared/org/queries';
import { лишниеЧасыПоста } from '../post-hours';
import { sitesHref } from '../route';

/** Заведение поста — то же право, что и на объект: правка справочников. */
const EDIT_PERMISSION = 'core.dictionary.edit';

/** Разряды охранника из тз: четвёртый, пятый и шестой. Другого в законе нет. */
const GUARD_CATEGORIES = ['4', '5', '6'] as const;

type FieldName = 'name' | 'hours_per_day' | 'simultaneous_guards';

export function NewPostPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = can(permissions, EDIT_PERMISSION);
  const site = useSiteQuery(id);

  const режимы = useScheduleTypesQuery();
  const [idempotencyKey] = useState(newIdempotencyKey);
  const [name, setName] = useState('');
  const [hours, setHours] = useState('24');
  const [category, setCategory] = useState(NO_VALUE);
  const [режим, setРежим] = useState(NO_VALUE);
  const [guards, setGuards] = useState('1');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [gateNote, setGateNote] = useState<string | null>(null);

  const mutation = useCreatePostMutation(id, idempotencyKey);
  const лишние = лишниеЧасыПоста(hours, Number(guards));

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setGateNote(
        'Заводить посты может тот, у кого есть право на правку справочников. Попросите администратора выдать это право.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Пост уже создаётся, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const next: Partial<Record<FieldName, string>> = {};
    if (name.trim() === '') {
      next.name = 'Введите название поста — например «КПП» или «Периметр».';
    }

    let hoursValue: DecimalString | null = null;
    try {
      hoursValue = decimalString(hours.trim().replace(',', '.'));
    } catch {
      next.hours_per_day = 'Часы пишутся числом: 12, 18, 24 или 63.5.';
    }

    const guardsValue = Number(guards);
    if (!Number.isInteger(guardsValue) || guardsValue < 1) {
      next.simultaneous_guards = 'Одновременно на посту стоит хотя бы один человек.';
    }

    if (Object.keys(next).length > 0 || hoursValue === null) {
      setErrors(next);
      return;
    }

    setErrors({});
    setGateNote(null);
    const body: PostIn = {
      name: name.trim(),
      hours_per_day: hoursValue,
      required_guard_category: category === NO_VALUE ? null : Number(category),
      // Режим работы: по нему СТРОИТСЯ ГРАФИК СМЕН (сценарий 17). Пока задать его было негде,
      // «создать график» молча не создавал ничего — у поста без режима смены не строятся, и
      // это правильно, но для человека выглядело сломанной кнопкой.
      schedule_type: режим === NO_VALUE ? null : режим,
      simultaneous_guards: guardsValue,
    };
    mutation.mutate(body, {
      onSuccess: () => {
        const search = popPanel(parseSearch(location.search));
        navigate(sitesHref('objects', { ...search, tab: 'posts' }, id));
      },
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
      <p className='text-paragraph-xs text-text-sub-600'>
        Объект: {site.data?.name ?? 'загружаем название…'}
      </p>

      {!allowed && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку справочников у вас нет: форму можно заполнить, но сохранить пост не
            выйдет. Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <div className='flex flex-col gap-1'>
        <Label.Root htmlFor='new-post-name'>
          Название поста
          <Label.Asterisk />
        </Label.Root>
        <Input.Root size='small' hasError={errors.name !== undefined}>
          <Input.Wrapper>
            <Input.Input
              id='new-post-name'
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder='Например: КПП'
              aria-invalid={errors.name !== undefined}
            />
          </Input.Wrapper>
        </Input.Root>
        {errors.name !== undefined && (
          <Hint.Root hasError>
            <Hint.Icon as={RiErrorWarningLine} />
            {errors.name}
          </Hint.Root>
        )}
      </div>

      <div className='flex flex-col gap-1'>
        <Label.Root htmlFor='new-post-hours'>
          Часов в сутки
          <Label.Asterisk />
        </Label.Root>
        <Input.Root size='small' hasError={errors.hours_per_day !== undefined}>
          <Input.Wrapper>
            <Input.Icon as={RiTimeLine} />
            <Input.Input
              id='new-post-hours'
              inputMode='decimal'
              value={hours}
              onChange={(event) => setHours(event.target.value)}
              aria-invalid={errors.hours_per_day !== undefined}
            />
          </Input.Wrapper>
        </Input.Root>
        {errors.hours_per_day === undefined ? (
          <Hint.Root>Круглосуточный пост — 24, дневной — 12. Встречается и 63.5.</Hint.Root>
        ) : (
          <Hint.Root hasError>
            <Hint.Icon as={RiErrorWarningLine} />
            {errors.hours_per_day}
          </Hint.Root>
        )}
      </div>
      {лишние !== null && (
        <Alert.Root variant='lighter' status='warning' size='xsmall' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>{лишние} Создать пост это не мешает: решать вам.</span>
        </Alert.Root>
      )}

      <SelectField
        id='new-post-schedule'
        label='Режим работы'
        value={режим}
        onChange={setРежим}
        options={(режимы.data?.items ?? []).map((строка) => ({
          value: строка.code,
          label: строка.title,
        }))}
        noneLabel='Режим не задан'
        placeholder='Выберите режим'
        pending={режимы.isPending}
        failed={режимы.isError}
        failedError={режимы.error as ApiError | null}
        hint='По режиму строится график смен в табеле. Не задан или «свой график» — смены заполняются вручную либо итогом за месяц.'
      />

      <SelectField
        id='new-post-category'
        label='Требуемый разряд'
        value={category}
        onChange={setCategory}
        options={GUARD_CATEGORIES.map((value) => ({ value, label: `${value} разряд` }))}
        noneLabel='Разряд не важен'
        placeholder='Выберите разряд'
        hint='Разряд ограничивает, кого можно поставить на пост.'
      />

      <div className='flex flex-col gap-1'>
        <Label.Root htmlFor='new-post-guards'>
          Одновременно охранников
          <Label.Asterisk />
        </Label.Root>
        <Input.Root size='small' hasError={errors.simultaneous_guards !== undefined}>
          <Input.Wrapper>
            <Input.Input
              id='new-post-guards'
              type='number'
              min={1}
              step={1}
              value={guards}
              onChange={(event) => setGuards(event.target.value)}
              aria-invalid={errors.simultaneous_guards !== undefined}
            />
          </Input.Wrapper>
        </Input.Root>
        {errors.simultaneous_guards === undefined ? (
          <Hint.Root>Из этого числа складывается требуемая укомплектованность объекта.</Hint.Root>
        ) : (
          <Hint.Root hasError>
            <Hint.Icon as={RiErrorWarningLine} />
            {errors.simultaneous_guards}
          </Hint.Root>
        )}
      </div>

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {gateNote !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {gateNote}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Создаём пост…' : 'Создать пост'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
